import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { AlertTriangle, Loader2, Save, X } from 'lucide-react';
import { ConfirmDialog } from './ConfirmDialog';
import {
  OrganizationDetail,
  updateOrganization,
  deleteOrganization,
} from '@/services/orgTeamApi';

interface OrganizationTabProps {
  organization: OrganizationDetail;
  userRole: string;
  isSuperadmin: boolean;
  onUpdate: () => void;
}

export function OrganizationTab({
  organization,
  userRole,
  isSuperadmin,
  onUpdate,
}: OrganizationTabProps) {
  const { toast } = useToast();

  const isOwner = userRole === 'owner';
  const isAdmin = userRole === 'admin' || isOwner;
  const canEdit = isAdmin || isSuperadmin;
  const canDelete = isOwner || isSuperadmin;

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: organization.name,
    description: organization.description || '',
  });
  const [isSaving, setIsSaving] = useState(false);

  // Delete state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSave = async () => {
    if (!editData.name.trim()) {
      toast({
        title: 'Error',
        description: 'Organization name is required',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      await updateOrganization(organization.id, {
        name: editData.name.trim(),
        description: editData.description.trim() || undefined,
      });

      toast({
        title: 'Organization updated',
        description: 'Organization details have been saved',
      });

      setIsEditing(false);
      onUpdate();
    } catch (error: any) {
      console.error('Failed to update organization:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update organization',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteOrganization(organization.id);

      toast({
        title: 'Organization archived',
        description: 'The organization has been archived',
      });

      // Redirect or refresh
      window.location.href = '/';
    } catch (error: any) {
      console.error('Failed to delete organization:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to archive organization',
        variant: 'destructive',
      });
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleCancelEdit = () => {
    setEditData({
      name: organization.name,
      description: organization.description || '',
    });
    setIsEditing(false);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Organization Details Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Organization Details</CardTitle>
              <CardDescription>
                Manage your organization's basic information
              </CardDescription>
            </div>
            {canEdit && !isEditing && (
              <Button variant="outline" onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isEditing ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={editData.name}
                  onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={editData.description}
                  onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <Save className="mr-2 h-4 w-4" />
                  Save
                </Button>
                <Button variant="outline" onClick={handleCancelEdit} disabled={isSaving}>
                  <X className="mr-2 h-4 w-4" />
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Name</Label>
                  <p className="font-medium">{organization.name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Slug</Label>
                  <p className="font-medium text-muted-foreground">{organization.slug}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Created</Label>
                  <p className="font-medium">{formatDate(organization.created_at)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div>
                    <Badge variant={organization.is_active ? 'default' : 'secondary'}>
                      {organization.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Members</Label>
                  <p className="font-medium">{organization.member_count}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Your Role</Label>
                  <div>
                    <Badge variant="outline" className="capitalize">{userRole}</Badge>
                  </div>
                </div>
              </div>
              {organization.description && (
                <div>
                  <Label className="text-muted-foreground">Description</Label>
                  <p className="mt-1">{organization.description}</p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Danger Zone Card (Owner/Superadmin only) */}
      {canDelete && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Danger Zone
            </CardTitle>
            <CardDescription>
              Irreversible actions that affect the entire organization
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">Archive this organization</p>
                <p className="text-sm text-muted-foreground">
                  Once archived, the organization will be deactivated. This action can only be undone by an administrator.
                </p>
              </div>
              <Button
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
              >
                Archive Organization
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Archive Organization"
        description={`Are you sure you want to archive "${organization.name}"? This will deactivate the organization and all associated teams. Members will lose access.`}
        confirmLabel="Archive"
        variant="destructive"
        isLoading={isDeleting}
        onConfirm={handleDelete}
      />
    </div>
  );
}
